import { Column } from "@faire/web--source/slate/Layout";
import { IBannerComponent } from "@faire/web-api--source/indigofair/data/IBannerComponent";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
// eslint-disable-next-line no-restricted-imports
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import React from "react";

import {
  objectFitCoverImageStyles,
  SDUIImage,
} from "@faire/retailer/components/SDUI/__internal__/Image/SDUIImage";
import { getCssAlignmentValue } from "@faire/retailer/components/SDUI/__internal__/Styling/getCssAlignmentValue";
import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveValueAtIndex,
} from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer/components/SDUI/__internal__/types";
import * as styles from "@faire/retailer/components/SDUI/ComponentRenderers/Banner/__internal__/AnnouncementBannerRenderer/styles/SDUIAnnouncementBannerRenderer.css";
import { ANNOUNCEMENT_BANNER_HEIGHTS } from "@faire/retailer/components/SDUI/ComponentRenderers/Banner/__internal__/AnnouncementBannerRenderer/styles/SDUIAnnouncementBannerRenderer.css";
import { SDUIBannerTextContent } from "@faire/retailer/components/SDUI/ComponentRenderers/Banner/__internal__/BannerTextContent/SDUIBannerTextContent";

const OPTIMIZE_IMAGE_OPTIONS = {
  mobileAndAbove: {
    height: ANNOUNCEMENT_BANNER_HEIGHTS.mobileAndAbove,
  },
  tabletAndAbove: {
    height: ANNOUNCEMENT_BANNER_HEIGHTS.tabletAndAbove,
  },
  desktopAndAbove: {
    height: ANNOUNCEMENT_BANNER_HEIGHTS.desktopAndAbove,
  },
};

type SDUIAnnouncementBannerProps = {
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  variant: "standard" | "constrained";
};

type ExternalFacingAnnouncementBannerType = Omit<
  SDUIAnnouncementBannerProps,
  "variant"
>;
export const SDUIDefaultAnnouncementBannerRenderer = (
  props: ExternalFacingAnnouncementBannerType
) => {
  return <BaseSDUIAnnouncementBannerRenderer {...props} variant="standard" />;
};

export const SDUIConstrainedWidthAnnouncementBannerRenderer = (
  props: ExternalFacingAnnouncementBannerType
) => {
  return (
    <BaseSDUIAnnouncementBannerRenderer {...props} variant="constrained" />
  );
};

const BaseSDUIAnnouncementBannerRenderer = ({
  bannerComponent,
  loggingData,
  renderBehaviour,
  variant,
}: SDUIAnnouncementBannerProps) => {
  const { images, responsive_images, vertical_content_alignment } =
    bannerComponent;

  const responsiveImages =
    convertServerResponsivePropertyToClientResponsiveProperty(
      images,
      (img) => img,
      responsive_images
    );

  return (
    <Column
      className={classNames(styles.announcementBannerWrapper)}
      style={assignInlineVars({
        [styles.verticalContentAlignment]: `${getCssAlignmentValue(
          vertical_content_alignment,
          "center"
        )}`,
      })}
    >
      <SDUIImage
        responsiveImage={getResponsiveValueAtIndex(
          responsiveImages,
          0,
          IImage.build()
        )}
        optimizeImageOptions={OPTIMIZE_IMAGE_OPTIONS}
        renderBehaviour={renderBehaviour}
        className={styles.backgroundImageWrapper}
        imageStyles={objectFitCoverImageStyles}
      />
      <SDUIBannerTextContent
        className={classNames(
          styles.announcementBannerContent,
          variant === "constrained"
            ? styles.constrainedWidthContent
            : styles.standardWidthContent
        )}
        bannerComponent={bannerComponent}
        loggingData={loggingData}
      />
    </Column>
  );
};
